import type { IconProps } from "phosphor-react";
import * as Icons from "phosphor-react";
import { type ComponentType, memo, useMemo } from "react";

const aliasMap: Record<string, keyof typeof Icons> = {
  account: "UserCircle",
  alert: "WarningCircle",
  back: "ArrowLeft",
  chat: "ChatsCircle",
  checklist: "ListChecks",
  "chevron-down": "CaretDown",
  "chevron-right": "CaretRight",
  close: "X",
  copy: "Copy",
  language: "GlobeHemisphereEast",
  sun: "SunDim",
  moon: "MoonStars",
  monitor: "Monitor",
  sidebar: "SidebarSimple",
  download: "DownloadSimple",
  edit: "PencilSimple",
  error: "WarningOctagon",
  help: "Question",
  history: "ClockCounterClockwise",
  information: "Info",
  "life-buoy": "Lifebuoy",
  menu: "List",
  refresh: "ArrowClockwise",
  plus: "Plus",
  search: "MagnifyingGlass",
  settings: "GearSix",
  share: "ShareNetwork",
  success: "CheckCircle",
  upload: "UploadSimple",
  warning: "Warning",
  sparkle: "Sparkle",
  "book-open": "BookOpen",
};

const iconComponents: Record<string, ComponentType<IconProps>> = Object.entries(
  Icons,
).reduce(
  (accumulator, [key, value]) => {
    if (key === "IconContext") {
      return accumulator;
    }
    if (
      typeof value === "function" ||
      (value && typeof value === "object" && "$$typeof" in value)
    ) {
      accumulator[key] = value as ComponentType<IconProps>;
    }
    return accumulator;
  },
  {} as Record<string, ComponentType<IconProps>>,
);

function toPascalCase(value: string) {
  return value
    .replace(/[-_\s]+(.)?/g, (_, group1) =>
      group1 ? group1.toUpperCase() : "",
    )
    .replace(/^(.)/, (match) => match.toUpperCase());
}

function resolveIconName(name: string): string | undefined {
  const normalized = name.trim();
  const alias = aliasMap[normalized.toLowerCase()];
  if (alias) {
    return alias;
  }

  const candidates = [
    normalized,
    toPascalCase(normalized),
    normalized.replace(/^(.)/, (match) => match.toUpperCase()),
  ];

  for (const candidate of candidates) {
    if (candidate in iconComponents) {
      return candidate;
    }
  }

  return undefined;
}

export interface IconComponentProps extends IconProps {
  name: string;
}

export const Icon = memo(function Icon({ name, ...props }: IconComponentProps) {
  const IconComponent = useMemo(() => {
    const resolvedName = resolveIconName(name);
    if (!resolvedName) {
      console.error(`[Icon] Unknown icon name: "${name}"`);
      return null;
    }
    return iconComponents[resolvedName] as ComponentType<IconProps>;
  }, [name]);

  if (!IconComponent) {
    return null;
  }

  return <IconComponent {...props} />;
});
